<template>
  <div
    v-if="blok"
    ref="promotionRoot"
    v-editable
    class="flex w-full text-center text-base font-medium"
    :class="[
      marginClasses,
      {
        'justify-start': alignment === 'left',
        'justify-center': alignment === 'centered',
        'justify-end': alignment === 'right',
      },
    ]"
  >
    <CMSLink
      :cms-link="blok.ctaLink"
      :open-new-tab="blok.openNewTab"
      :product-id="blok.product_id"
      :order-number="blok.order_number"
      :aria-label="ctaAriaLabel"
      data-test-id="cta-button"
      class="pointer-events-auto flex items-center justify-center gap-2"
      :class="[
        {
          'mb-1 p-1 text-base tracking-widest underline hover:text-black-50':
            blok.ctaDesign === 'textButton',
          'group px-2 py-3': blok.ctaDesign !== 'textButton',
        },
        {
          'w-[200px] bg-black shadow-button hover:bg-primary-400 hover:shadow-none':
            blok.ctaDesign === 'mainButtonBlack',
          'w-full bg-black shadow-button hover:bg-primary-400 hover:shadow-none':
            blok.ctaDesign === 'mainButtonBlackWholeWidth',
          'w-[200px] bg-white shadow-lg hover:bg-lightGray md:shadow-xl':
            blok.ctaDesign === 'mainButtonWhiteSmall',
          'w-full bg-white shadow-lg hover:bg-lightGray md:shadow-xl':
            blok.ctaDesign === 'mainButtonWhiteLarge',
        },
        {
          'text-white':
            blok.textColor === 'white' ||
            ((blok.ctaDesign === 'mainButtonBlack' ||
              blok.ctaDesign === 'mainButtonBlackWholeWidth') &&
              !blok.textColor),
        },
        {
          'text-black':
            blok.textColor === 'black' ||
            ((blok.ctaDesign === 'mainButtonWhiteSmall' ||
              blok.ctaDesign === 'mainButtonWhiteLarge') &&
              !blok.textColor),
        },
        'text-sm leading-5',
        '[@media(min-width:1520px)]:text-base [@media(min-width:1520px)]:leading-6',
        '[@media(min-width:1520px)]:w-[220px]',
        '[@media(min-width:1680px)]:w-[240px]',
        '[@media(min-width:1800px)]:w-[260px]',
      ]"
      @click="handlePromotionSelect"
    >
      <span class="max-w-full">{{ blok.ctaLabel }}</span>
      <span v-if="ctaHiddenContext" class="sr-only">
        {{ ctaHiddenContext }}
      </span>
      <span class="sr-only">{{ getTargetBlankLabel('', $t) }}</span>
    </CMSLink>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import type { PropType } from 'vue'
import CMSLink from '~/storyblok/link/CMSLink.vue'
import type { SbCtaButton } from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { getTargetBlankLabel } from '#layers/base/utils/a11y'
import { CTA_PROMOTION_CONTEXT_KEY } from '#layers/base/constants/injectionKeys'
import { resolveCtaPromotion } from '#layers/base/utils/ctaPromotionTracking'

const props = defineProps({
  blok: {
    type: Object as PropType<SbCtaButton>,
    default: () => ({}),
  },
  // Passed down by Grid and Column, so a CTA in a grid reports its slot.
  siblingIndex: {
    type: Number,
    default: 0,
  },
})

const { marginClasses } = useStoryblokMargins(props.blok)
const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()

// Stays null until the MAD Storyblok space (265080) gets the promotion fields
// the AGS CTA Button already has, so this whole path is dormant here for now.
const promotion = resolveCtaPromotion({
  inherited: inject(CTA_PROMOTION_CONTEXT_KEY, undefined),
  blok: props.blok,
  position: props.siblingIndex + 1,
})

const promotionRoot = ref<HTMLElement | null>(null)

if (promotion) {
  // Observing the existing root instead of wrapping in Intersect keeps the DOM
  // unchanged: an extra shrink-to-fit box would collapse the whole-width CTA
  // designs.
  const { stop } = useIntersectionObserver(
    promotionRoot,
    ([entry]) => {
      if (entry?.isIntersecting) {
        trackPromotionView(promotion)
        stop()
      }
    },
    { threshold: 0.5 },
  )
}

const handlePromotionSelect = promotion
  ? () => trackPromotionSelect(promotion)
  : undefined

const ctaDestinationLabel = computed(() => {
  const url = props.blok?.ctaLink?.cached_url || ''
  const [path = ''] = url.split('?')
  const [slug = ''] = path.split('/').filter(Boolean).slice(-1)

  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
})

const ctaHiddenContext = computed(() => {
  const label = props.blok?.ctaLabel?.trim() || ''

  if (!label || !/^(mehr|more)$/i.test(label)) {
    return ''
  }

  const destination = ctaDestinationLabel.value

  return destination ? ` ${destination}` : ''
})

const ctaAriaLabel = computed(
  () =>
    `${props.blok?.ctaLabel || ''}${ctaHiddenContext.value}`.trim() ||
    undefined,
)

const alignment = computed(() => props.blok?.alignment || '')
</script>
